import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Button, 
  Container, 
  Grid, 
  Typography, 
  Paper,
  MenuItem,
  Select,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  CircularProgress,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Checkbox,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import PageBackground from '../components/PageBackground';
import FocusRetainingTextField from '../components/FocusRetainingTextField';
import { useForm, Controller } from 'react-hook-form';
import HookFormTextField from '../components/HookFormTextField';
import FormHeader from '../components/FormHeader';
import PreliminaryChecklist from '../components/PreliminaryChecklist';
import IncomeAccordion from '../components/IncomeAccordion'; // Import the income accordion component
import BorrowerAccordion from '../components/BorrowerAccordion'; // Import the borrower accordion component
import PropertyAccordion from '../components/PropertyAccordion'; // Import the property accordion component
import DownpaymentAccordion from '../components/DownpaymentAccordion'; // Import the downpayment accordion component
import ConditionsAccordion from '../components/ConditionsAccordion'; // Import the conditions accordion component
import { submitBorrowerForm } from '../services/borrowerFormService';

// Define interfaces for our form data
export interface BorrowerData {
  name: string;
  aka: string;
  hasAKA: string;
  akaReason?: string;
  dob: string;
  hasSIN: string;
  relationshipStatus: string; // Added
  relationshipOther?: string; // Added for conditional field
  dobMerlin: string; // ADDED
  sinCbStatus: string; // ADDED
  beacon: string;
  dateCBPulled: string;
  agentForCB: string;
  inquiries: string;
  mtg_inquiry_reason: string;
  other_mtg_inquiry_reason?: string;
  address: string;
  creditHistoryNotes: string;
  otherCreditHistoryNotes?: string;
  adverseMedia: string;
  warningNoted: string;
  warningNotes?: string;
  employmentOnCB: string;
  repaymentHistory: string;
  otherRepaymentHistory?: string;
  sinceDateStatus: string; // ADDED to BorrowerData
  sinceDateOtherNotes?: string; // ADDED to BorrowerData
  overallProfile: string; // Added field
  overallProfileOther?: string; // Added field
  pr_rt: string;
  rt_description: string;
  childSupport: boolean; // New checkbox field
  noChildSupport: boolean; // New checkbox field
  statDecForSeparation: boolean; // New checkbox field
  bankruptcy: boolean; // BKRPT checkbox
  cpDischarged: boolean; // CP Discharged checkbox
  cpActive: boolean; // CP Active checkbox
  notOnTitle: string; // Not on Title text field
}

export interface RentalProperty {
  address: string;
  newBuild: boolean; // Added for New Build checkbox
  purchasePrice: string; // Added for Purchase Price
  propertyType: string;
  rentalIncome: string;
  propertyTax: string;
  condoFees: string;
  mortgagePayment: string;
  mortgageAmount: string; // Added for Mortgage amount
  heat: string; // Added
  addThreePercent: boolean; // Added
  addOnePercentPP: boolean; // Added for +1% PP checkbox
  isSubjectOO: boolean; // Added for Subject Owner-Occupied
  isSubjectSecondHome: boolean; // Added for Subject Second Home
  isSecondHome: boolean; // Added for Second Home
  ownedPercentB1: string;
  ownedPercentB2: string;
  ownedPercentB3: string;
  ownedPercentB4: string;
  rentalSurplusDeficit: string; // Fixed: Made non-optional as it has a default
  calculateSurplus: boolean; // Added state for checkbox
  useNinetyFivePercentIncome: boolean; // Added for 95% checkbox
  calculatedTaxPlus3Percent: string; // Removed optional flag (?)
  calculatedOnePercentPP: string; // Removed optional flag (?)
  marketRent: boolean; // Market Rent checkbox
  leaseAgreement: boolean; // Lease Agreement checkbox
  noticeToVacate: boolean; // Notice to vacate checkbox
  beingSoldForDP: boolean; // Being sold for DP checkbox
  refiForDP: boolean; // Refi for DP checkbox
  bridgeAB: boolean; // Bridge AB checkbox
  bridgeBC: boolean; // Bridge BC checkbox
  outOfProvince: boolean; // Added for Out of province checkbox
  [key: string]: string | boolean; // Allow dynamic access with string keys, accommodate boolean
}

export interface Income {
  incomeDescription: string;
  annualIncome: string;  // Added field for annual income
  employer: string;
  job_description: string;
  tenure: string;
  timeInIndustry: string;
  bfsYn: string;
  borrowerId: string;
  [key: string]: string; // Allow dynamic access with string keys
}

// Make FormState exportable
export interface FormState {
  dealNumber: string;
  dealType: string;
  closingDate: string;
  cof: string;
  bfsStage: string;
  propertyWatchlistStatus: string;
  employerWatchlistStatus: string;
  matchFilesStatus: string;
  matchFilesNotes?: string;
  propertyTaxAdjustment: string;
  dealBorrowerRelationshipStatus: string;
  dealBorrowerRelationshipOther?: string;
  propertyValueStatus: string;
  propertyValueOtherNotes?: string;
  mortgageAmount: string;
  mtgType: string;
  ltv: string;
  etoChecked: boolean;
  etoNotes: string;
  term: string;
  rate: string;
  amortization: string;
  ltvRestrictions: string;
  propertyInfo: string;
  province: string; // Added new province field
  downpaymentSource: string; // Changed from downpaymentSources: { [key: string]: boolean }
  downpaymentSourceNotes: string; // New field for additional notes
  downpaymentAmount: string; // Added for Downpayment Amount
  giftor: string; // New field for giftor
  inheritingFrom: string; // New field for inheriting from
  savingsStatus: string;
  sinceDateStatus: string;
  sinceDateOtherNotes?: string;
  borrowers: { [key: string]: BorrowerData };
  rentalProperties: { [key: string]: RentalProperty };
  incomes: { [key: string]: Income };
  conditions: { [key: string]: boolean };
  elapsedTime?: number;
  startTime?: string;
  endTime?: string;
  submitAttempted?: boolean;
}

// Initialize empty borrower data
const emptyBorrowerData: BorrowerData = {
  name: '',
  aka: '',
  hasAKA: 'No',
  akaReason: '',
  dob: '',
  hasSIN: 'No',
  relationshipStatus: '',
  relationshipOther: '', // Initialize added field
  dobMerlin: '',
  sinCbStatus: '',
  beacon: '',
  dateCBPulled: '',
  agentForCB: 'Active',
  inquiries: '',
  mtg_inquiry_reason: '',
  other_mtg_inquiry_reason: '',
  address: 'Match',
  creditHistoryNotes: 'RS',
  otherCreditHistoryNotes: '',
  adverseMedia: 'No',
  warningNoted: '',
  warningNotes: '',
  employmentOnCB: '',
  repaymentHistory: '',
  otherRepaymentHistory: '',
  sinceDateStatus: '',
  sinceDateOtherNotes: '',
  overallProfile: '', // Added field
  overallProfileOther: '', // Added field
  pr_rt: '',
  rt_description: '',
  childSupport: false, // New checkbox field
  noChildSupport: false, // New checkbox field
  statDecForSeparation: false, // New checkbox field
  bankruptcy: false, // BKRPT checkbox
  cpDischarged: false, // CP Discharged checkbox
  cpActive: false, // CP Active checkbox
  notOnTitle: '', // Not on Title text field
};

// Initialize empty rental property data
const emptyRentalProperty: RentalProperty = {
  address: '',
  newBuild: false, // Added default
  purchasePrice: '', // Added default
  propertyType: '',
  rentalIncome: '',
  propertyTax: '',
  condoFees: '',
  mortgagePayment: '',
  mortgageAmount: '', // Added default
  heat: '', // Added default
  addThreePercent: false, // Added default
  addOnePercentPP: false, // Added default
  isSubjectOO: false, // Added default
  isSubjectSecondHome: false, // Added default
  isSecondHome: false, // Added default
  ownedPercentB1: '0',
  ownedPercentB2: '0',
  ownedPercentB3: '0',
  ownedPercentB4: '0',
  rentalSurplusDeficit: '0.00', // Added default
  calculateSurplus: false, // Added default
  useNinetyFivePercentIncome: false, // Added default
  calculatedTaxPlus3Percent: '0.00', // Initialize calculation field
  calculatedOnePercentPP: '0.00', // Initialize calculation field
  marketRent: false, // Market Rent checkbox
  leaseAgreement: false, // Lease Agreement checkbox
  noticeToVacate: false, // Notice to vacate checkbox
  beingSoldForDP: false, // Being sold for DP checkbox
  refiForDP: false, // Refi for DP checkbox
  bridgeAB: false, // Bridge AB checkbox
  bridgeBC: false, // Bridge BC checkbox
  outOfProvince: false, // Added default
};

// Initialize empty income data
const emptyIncome: Income = {
  incomeDescription: '',
  annualIncome: '',  // Default empty annual income
  employer: '',
  job_description: '',
  tenure: '',
  timeInIndustry: '',
  bfsYn: 'No',
  borrowerId: '',
};

// Update the props interface to expect the correct payload type
interface BorrowerFormProps {
  onSubmit: (formData: DealSubmissionPayload) => Promise<boolean>; // Changed FormState to DealSubmissionPayload
}

// Function to generate styled input components
const getStyledComponents = () => {
  const inputSx = { 
    '& .MuiInputBase-root': {
      height: '36px',
      margin: 0,
      padding: 0
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.85rem',
      transform: 'translate(14px, 9px) scale(1)'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    },
    my: 0.2 // Add margin top and bottom
  };
  
  // Restore the original implementation
  const StyledTextField = (props: any) => (
    <FocusRetainingTextField 
      {...props} 
      size="small" 
      sx={{...inputSx, ...props.sx}}
      autoComplete="off"
    />
  );
  
  const StyledFormControl = (props: any) => (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      my: 0.2,
      px: 0.5 // Add consistent horizontal padding
    }}>
      <Box sx={{ width: '100%' }}>
        <FormControl 
          {...props} 
          size="small" 
          sx={{
            width: '100%',
            '& .MuiInputBase-root': {
              height: '36px',
              margin: 0,
              padding: 0
            },
            '& .MuiSelect-select': {
              padding: '8px 14px',
              height: '20px',
              boxSizing: 'border-box'
            },
            '& .MuiFormLabel-root': {
              fontSize: '0.85rem',
              transform: 'translate(14px, 9px) scale(1)'
            },
            '& .MuiFormLabel-filled, & .Mui-focused': {
              transform: 'translate(14px, -6px) scale(0.75)'
            },
            ...(props.sx || {})
          }} 
        />
      </Box>
    </Box>
  );
  
  return { StyledTextField, StyledFormControl, inputSx };
};

// Special component for borrower name field using React Hook Form
const BorrowerNameField = ({ 
  borrowerId, 
  name, 
  onChange, 
  error, 
  helperText 
}: { 
  borrowerId: string; 
  name: string; 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
}) => {
  const { control, setValue } = useForm({
    defaultValues: {
      [borrowerId]: name
    }
  });
  
  const hookFormActive = useRef(true);
  
  useEffect(() => {
    if (!hookFormActive.current) {
      setValue(borrowerId, name);
    }
  }, [name, borrowerId, setValue]);
  
  return (
    <Controller
      name={borrowerId}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label="Name"
          fullWidth
          required
          size="small"
          autoComplete="off"
          error={error}
          helperText={helperText}
          sx={{ 
            '& .MuiInputBase-root': { 
              height: '36px'
            },
            '& .MuiOutlinedInput-input': {
              padding: '8px 14px',
              height: '20px',
              boxSizing: 'border-box'
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.85rem',
              transform: 'translate(14px, 9px) scale(1)'
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)'
            }
          }}
          onChange={(e) => {
            hookFormActive.current = true;
            field.onChange(e);
            const syntheticEvent = {
              ...e,
              target: e.target as HTMLInputElement
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
            setTimeout(() => {
              hookFormActive.current = false;
            }, 0);
          }}
        />
      )}
    />
  );
};

// Helper function to safely parse currency string to number
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const parseCurrency = (value: string | undefined | null): number => {
  if (!value) return 0;
  return parseFloat(String(value).replace(/,/g, '')) || 0;
};

// Helper function to calculate Rental Surplus/Deficit
// Commented out as currently unused but may be needed in the future
/* const calculateRentalSurplusDeficit = (property: RentalProperty): number => {
  const rentalIncome = parseCurrency(property.rentalIncome);
  const propertyTax = parseCurrency(property.propertyTax);
  const condoFees = parseCurrency(property.condoFees);
  const mortgagePayment = parseCurrency(property.mortgagePayment);
  const heat = parseCurrency(property.heat);

  const monthlyTax = property.addThreePercent 
    ? (propertyTax * 1.03) / 12 
    : propertyTax / 12;

  const ownershipB1 = parseCurrency(property.ownedPercentB1) / 100;
  const ownershipB2 = parseCurrency(property.ownedPercentB2) / 100;
  const ownershipB3 = parseCurrency(property.ownedPercentB3) / 100;
  const ownershipB4 = parseCurrency(property.ownedPercentB4) / 100;
  const totalOwnership = ownershipB1 + ownershipB2 + ownershipB3 + ownershipB4;
  
  // Handle potential division by zero or invalid total ownership
  if (totalOwnership <= 0) {
      return 0; 
  }

  const surplusDeficit = (rentalIncome * 0.95 - monthlyTax - condoFees - mortgagePayment - heat) * totalOwnership;
  
  return surplusDeficit;
}; */

// Define conditions labels and keys
const conditionItems = [
  { key: 'financialMgm', label: 'Financial MGM' },
  { key: 'removeFromTitle', label: 'Remove from Title' },
  { key: 'ila', label: 'ILA' },
  { key: 'condoFinancials', label: 'Condo Financials' },
  { key: 'postTensionCable', label: 'Post Tension Cable' },
  { key: 'duplex2Units', label: 'Duplex 2 Units' },
  { key: 'purPermitLt183D', label: 'PUR Permit <183D' },
  { key: 'refiTempResident', label: 'REFI Temp Resident' },
  { key: 'newToCanada', label: 'New to Canada' },
  { key: 'holdBack', label: 'Hold Back' },
  { key: 'bridgeON', label: 'Bridge ON' },
];

// Function to create initial conditions state
const createInitialConditions = (): { [key: string]: boolean } => {
  const initialState: { [key: string]: boolean } = {};
  conditionItems.forEach(item => {
    initialState[item.key] = false;
  });
  return initialState;
};

// Define Downpayment Source options
const downpaymentSourceOptions = [
  { key: 'na', label: 'N/A' },
  { key: 'savings', label: 'Savings' },
  { key: 'saleProceedsOO', label: 'Sale Proceeds of O/O' },
  { key: 'saleProceedsRental', label: 'Sale proceeds of Rental Property' },
  { key: 'savingsGifted', label: 'Savings + Gifted Funds' },
  { key: 'savingsSaleProceeds', label: 'Savings + Sale Proceeds' },
  { key: 'saleProceedsGifted', label: 'Sale Proceeds + Gifted' },
  { key: 'inheritance', label: 'Inheritance' },
  { key: 'crypto', label: 'Crypto' },
  { key: 'gifted', label: 'Gifted' },
  { key: 'giftedDonorPossession', label: 'Gifted - Donor\'s possession' },
  { key: 'giftedLawyerPossession', label: 'Gifted Lawyer Possession' },
  { key: 'helocRefinance', label: 'Heloc/Refinance' },
  { key: 'giftedEquity', label: 'Gifted Equity' },
];

// Function to create initial downpayment sources state
// Commented out as currently unused
/* const createInitialDownpaymentSources = (): { [key: string]: boolean } => {
  const initialState: { [key: string]: boolean } = {};
  downpaymentSourceOptions.forEach(item => {
    initialState[item.key] = false;
  });
  return initialState;
}; */

// Interface for the final data payload sent to onSubmit/Supabase
interface DealSubmissionPayload extends Omit<FormState, 'downpaymentSource' | 'downpaymentSourceNotes' | 'giftor' | 'inheritingFrom'> {
  downpaymentSource: string;
  downpaymentSourceNotes: string;
  downpaymentAmount: string;
  giftor: string;
  inheritingFrom: string;
  ltvRestrictions: string; // Added missing field
}

const BorrowerForm: React.FC<BorrowerFormProps> = ({ onSubmit }) => {
  // Get styled components
  const { StyledFormControl } = getStyledComponents();

  // Initialize with 4 borrowers and 4 rental properties
  const [formState, setFormState] = useState<FormState>({
    dealNumber: '',
    dealType: 'PUR',
    closingDate: '',
    cof: '',
    bfsStage: '',
    // Default values for new fields
    propertyWatchlistStatus: '',
    employerWatchlistStatus: '',
    matchFilesStatus: 'No match files', // Default selection
    matchFilesNotes: '',
    propertyTaxAdjustment: '',
    dealBorrowerRelationshipStatus: '',
    dealBorrowerRelationshipOther: '',
    propertyValueStatus: '', // Added default
    propertyValueOtherNotes: '', // Added default
    mortgageAmount: '', // Added default
    mtgType: '', // Added default
    ltv: '', // Added default
    etoChecked: false, // Added default
    etoNotes: '', // Added default
    term: '', // Added default
    rate: '', // Added default
    amortization: '', // Added default
    ltvRestrictions: '', // Added default
    propertyInfo: '', // Added default
    province: '', // Added new province field
    downpaymentSource: '', // Changed from downpaymentSources: { [key: string]: boolean }
    downpaymentSourceNotes: '', // New field for additional notes
    downpaymentAmount: '', // Added for Downpayment Amount
    giftor: '', // New field for giftor
    inheritingFrom: '', // New field for inheriting from
    savingsStatus: '', // Added default
    sinceDateStatus: '', // Added default
    sinceDateOtherNotes: '', // Added default
    // Default nested states
    borrowers: { borrower1: { ...emptyBorrowerData } }, 
    rentalProperties: { property1: { ...emptyRentalProperty } },
    incomes: {}, // Initialize incomes as empty object
    conditions: createInitialConditions(), 
    submitAttempted: false,
    startTime: undefined,
  });

  // Timer and Timestamp state
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  // Handle time update from the TimerDisplay component
  const handleTimeUpdate = useCallback((seconds: number) => {
    setFormState(prev => ({ ...prev, elapsedTime: seconds }));
  }, []);
  
  // Listen for changes to formState.startTime
  useEffect(() => {
    if (formState.startTime && !startTime) {
      // Convert ISO string to Date object if needed
      try {
        const newStartTime = new Date(formState.startTime);
        setStartTime(newStartTime);
        console.log('Timer started at:', newStartTime);
      } catch (err) {
        console.error('Error parsing startTime:', err);
      }
    }
  }, [formState.startTime, startTime]);
  
  // Track active borrowers and properties
  const [activeBorrowers, setActiveBorrowers] = useState<string[]>(['borrower1']);
  const [activeProperties, setActiveProperties] = useState<string[]>(['property1']);
  const [activeIncomes, setActiveIncomes] = useState<string[]>(['income1']);
  const [incomeCounter, setIncomeCounter] = useState(1);

  // State for submission status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  
  // Expanded state for accordions
  const [expandedBorrower, setExpandedBorrower] = useState<string | false>(false);
  const [expandedProperty, setExpandedProperty] = useState<string | false>(false);
  const [expandedIncome, setExpandedIncome] = useState<string | false>(false);
  const [expandedConditions, setExpandedConditions] = useState<boolean>(false); // Added state for conditions accordion
  const [expandedDownpayment, setExpandedDownpayment] = useState<boolean>(false); // Added state
  const [expandedChecklist, setExpandedChecklist] = useState<boolean>(true); // Added state for Preliminary Checklist
  const [expandedSection, setExpandedSection] = useState<string>(''); // Added state for conditions accordion

  // Global refs for focus tracking
  // const lastActiveElementRef = useRef<Element | null>(null); // Commented out as currently unused

  // Add state for notifications
  const [notification, setNotification] = useState<{show: boolean; message: string; type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  // Handle borrower changes
  const handleBorrowerChange = useCallback((borrowerId: string, field: keyof BorrowerData) => 
    (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | SelectChangeEvent) => {
      setFormState(prev => ({
        ...prev,
        borrowers: {
          ...prev.borrowers,
          [borrowerId]: {
            ...prev.borrowers[borrowerId],
            [field]: e.target.value
          }
        }
      }));
  }, []);

  // Handle property changes (updated to handle boolean checkbox and direct string/boolean values)
  // This function is used in the JSX below, so we need to keep it
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePropertyChange = useCallback((propertyId: string, field: keyof RentalProperty, value: string | boolean) => {
    setFormState(prev => ({
      ...prev,
      rentalProperties: {
        ...prev.rentalProperties,
        [propertyId]: {
          ...prev.rentalProperties[propertyId],
          [field]: value
        }
      }
    }));
  }, []);

  // Handle income changes
  const handleIncomeChange = useCallback((incomeId: string, field: keyof Income) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFormState(prevState => ({
      ...prevState,
      incomes: {
        ...prevState.incomes,
        [incomeId]: {
          ...prevState.incomes[incomeId],
          [field]: value
        }
      }
    }));
  }, [setFormState]);
  
  // Handle deal type changes
  const handleDealTypeChange = useCallback((e: SelectChangeEvent) => {
    setFormState(prev => ({
      ...prev,
      dealType: e.target.value
    }));
  }, []);

  // Handle BFS Stage changes
  const handleBfsStageChange = useCallback((e: SelectChangeEvent) => {
    setFormState(prev => ({
      ...prev,
      bfsStage: e.target.value
    }));
  }, []);

  // Handle accordion expansion for borrowers
  const handleBorrowerAccordionChange = useCallback(
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedBorrower(isExpanded ? panel : false);
    }, []);

  // Handle accordion expansion for properties
  const handlePropertyAccordionChange = useCallback(
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedProperty(isExpanded ? panel : false);
    }, []);

  // Handle accordion expansion for incomes
  const handleIncomeAccordionChange = useCallback(
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedIncome(isExpanded ? panel : false);
    }, []);

  // Handle accordion expansion for conditions - Added
  const handleConditionsAccordionChange = useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedConditions(isExpanded);
    }, []);
    
  // Handle accordion expansion for Downpayment - Added
  // Commented out as currently unused
  /* const handleDownpaymentAccordionChange = useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedDownpayment(isExpanded);
    }, []); */

  // Handle accordion expansion for Preliminary Checklist - Added
  const handleChecklistAccordionChange = useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedChecklist(isExpanded);
    }, []);
    
  // Handle section changes for modular components
  const handleSectionChange = useCallback(
    (section: string) => () => {
      setExpandedSection(prev => prev === section ? '' : section);
    }, []);

  // ==== RESTORED HANDLER FUNCTIONS ==== 
  const handleAddBorrower = useCallback(() => {
    if (activeBorrowers.length < 4) {
      const nextBorrowerId = `borrower${activeBorrowers.length + 1}`;
      setActiveBorrowers(prev => [...prev, nextBorrowerId]);
      // ALSO add empty data to formState
      setFormState(prev => ({
        ...prev,
        borrowers: {
          ...prev.borrowers,
          [nextBorrowerId]: { ...emptyBorrowerData } // Add empty data structure
        }
      }));
      setExpandedBorrower(nextBorrowerId); // Expand the new one
    }
  }, [activeBorrowers, setActiveBorrowers, setFormState, setExpandedBorrower]);

  const handleAddProperty = useCallback(() => {
    if (activeProperties.length < 4) {
      const nextPropertyId = `property${activeProperties.length + 1}`;
      setActiveProperties(prev => [...prev, nextPropertyId]);
       // ALSO add empty data to formState
      setFormState(prev => ({
        ...prev,
        rentalProperties: {
          ...prev.rentalProperties,
          [nextPropertyId]: { ...emptyRentalProperty } // Add empty data structure
        }
      }));
      setExpandedProperty(nextPropertyId); // Expand the new one
    }
  }, [activeProperties, setActiveProperties, setFormState, setExpandedProperty]);

  const handleRemoveBorrower = useCallback((borrowerId: string) => {
    if (activeBorrowers.length > 1) {
      setActiveBorrowers(prev => prev.filter(id => id !== borrowerId));
      // Optionally remove borrower's incomes
      setFormState(prev => {
        const newIncomes = { ...prev.incomes };
        Object.keys(newIncomes).forEach(incomeId => {
          if (newIncomes[incomeId].borrowerId === borrowerId) {
            delete newIncomes[incomeId];
          }
        });
        const newBorrowers = { ...prev.borrowers };
        delete newBorrowers[borrowerId];
        return {
          ...prev,
          borrowers: newBorrowers,
          incomes: newIncomes
        };
      });
      if (expandedBorrower === borrowerId) {
        const remaining = activeBorrowers.filter(id => id !== borrowerId);
        setExpandedBorrower(remaining[0] || false);
      }
    }
  }, [activeBorrowers, expandedBorrower, setActiveBorrowers, setExpandedBorrower, setFormState]);

  const handleRemoveProperty = useCallback((propertyId: string) => {
    if (activeProperties.length > 1) {
      setActiveProperties(prev => prev.filter(id => id !== propertyId));
      setFormState(prev => {
        const newProperties = { ...prev.rentalProperties };
        delete newProperties[propertyId];
        return {
          ...prev,
          rentalProperties: newProperties
        };
      });
      if (expandedProperty === propertyId) {
        const remaining = activeProperties.filter(id => id !== propertyId);
        setExpandedProperty(remaining[0] || false);
      }
    }
  }, [activeProperties, expandedProperty, setActiveProperties, setExpandedProperty, setFormState]);

  // Modified to add income to a specific borrower
  const handleAddIncome = useCallback((borrowerId: string) => {
      const nextIncomeId = `income${incomeCounter + 1}`;
      setIncomeCounter(prev => prev + 1);
      setActiveIncomes(prev => [...prev, nextIncomeId]);
      setExpandedIncome(nextIncomeId);
      setFormState(prev => ({
        ...prev,
        incomes: {
          ...prev.incomes,
        [nextIncomeId]: { 
          ...emptyIncome,
          borrowerId: borrowerId  // Assign the income to this borrower
        }
    }
    }));
  }, [incomeCounter, setIncomeCounter, setActiveIncomes, setExpandedIncome, setFormState]);

  // Modified to handle removing income
  const handleRemoveIncome = useCallback((incomeId: string) => {
      setActiveIncomes(prev => prev.filter(id => id !== incomeId));
      if (expandedIncome === incomeId) {
        const remaining = activeIncomes.filter(id => id !== incomeId);
        setExpandedIncome(remaining[0] || false);
      }
      setFormState(prev => {
        const newIncomes = { ...prev.incomes };
        delete newIncomes[incomeId];
        return {
          ...prev,
          incomes: newIncomes
        };
      });
  }, [activeIncomes, expandedIncome, setActiveIncomes, setExpandedIncome, setFormState]);
  // ==== END RESTORED HANDLER FUNCTIONS ====

  // Handler to calculate surplus/deficit on button click
  // Commented out as currently unused but may be needed in the future
  /* const handleCalculateSurplusDeficit = (propertyId: string) => {
    console.log(`[Debug] handleCalculateSurplusDeficit called for propertyId: ${propertyId}`);
    const property = formState.rentalProperties[propertyId];
    console.log(`[Debug] Property data for ${propertyId}:`, property);

    if (property && property.propertyType === 'Rental') { 
      console.log(`[Debug] Property type is 'Rental'. Proceeding with calculation.`);
      // ... (logging intermediate values) ...
      const calculatedValue = calculateRentalSurplusDeficit(property).toFixed(2);
      console.log(`[Debug] Calculated surplus/deficit: ${calculatedValue}`);
      
      // Restore original setFormState call 
      setFormState(prev => {
        console.log(`[Debug] Updating state for ${propertyId} with surplus/deficit: ${calculatedValue}`);
        return {
          ...prev,
          rentalProperties: {
            ...prev.rentalProperties,
            [propertyId]: {
              ...prev.rentalProperties[propertyId],
              rentalSurplusDeficit: calculatedValue
            }
          }
        };
      });
      
    } else {
      console.log(`[Debug] Calculation skipped. Property type is not 'Rental' or property data missing.`);
    }
  }; */

  // ==== ADDED useEffect for Calculations ==== 
  useEffect(() => {
    // Recalculate and update state whenever dependencies change
    activeProperties.forEach(propertyId => {
      const property = formState.rentalProperties[propertyId];
      let updated = false;
      let newCalculatedTax = property.calculatedTaxPlus3Percent;
      let newCalculatedPP = property.calculatedOnePercentPP;

      // Calculate +3% Tax when checkbox is selected or property tax changes
      if (property.addThreePercent) {
        const tax = parseFloat(String(property.propertyTax || '0').replace(/,/g, ''));
        const calculatedTax = isNaN(tax) ? '0.00' : (tax * 1.03).toFixed(2);
        if (calculatedTax !== property.calculatedTaxPlus3Percent) {
          newCalculatedTax = calculatedTax;
          updated = true;
        }
      } else if (property.calculatedTaxPlus3Percent !== '0.00') {
        // Reset if checkbox is unchecked
        newCalculatedTax = '0.00';
        updated = true;
      }

      // Calculate +1% PP
      if (property.addOnePercentPP && property.newBuild) {
        const pp = parseFloat(String(property.purchasePrice || '0').replace(/,/g, ''));
        const calculatedPP = isNaN(pp) ? '0.00' : (pp * 0.01).toFixed(2);
         if (calculatedPP !== property.calculatedOnePercentPP) {
          newCalculatedPP = calculatedPP;
          updated = true;
        }
      } else if (property.calculatedOnePercentPP !== '0.00') {
         newCalculatedPP = '0.00'; // Reset if checkbox unchecked or not new build
         updated = true;
      }

      if (updated) {
        setFormState(prev => ({
          ...prev,
          rentalProperties: {
            ...prev.rentalProperties,
            [propertyId]: {
              ...prev.rentalProperties[propertyId],
              calculatedTaxPlus3Percent: newCalculatedTax,
              calculatedOnePercentPP: newCalculatedPP
            }
          }
        }));
      }
    });
  // Dependencies: watch relevant fields for all active properties
  }, [activeProperties, formState.rentalProperties]); 
  // Note: Watching the entire rentalProperties object might be too broad in complex scenarios,
  // but is simpler here. A more granular approach might watch specific fields like
  // [propertyId].propertyTax, [propertyId].addThreePercent, etc. for each propertyId.

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[Debug] Starting form submission');
    setFormState(prev => ({ ...prev, submitAttempted: true }));
    
    let hasError = false;
    let errorMsg = '';

    // Basic required fields validation
    if (!formState.dealNumber || !formState.dealType || !formState.closingDate) {
      errorMsg = 'Deal Number, Deal Type, and Closing Date are required.';
      hasError = true;
      console.log('[Debug] Missing required fields:', { dealNumber: formState.dealNumber, dealType: formState.dealType, closingDate: formState.closingDate });
    }

    // Mandatory Rental Income validation
    activeProperties.forEach(propertyId => {
      const property = formState.rentalProperties[propertyId];
      const isRentalType = [
        'Rental',
        'O/O + Rental',
        'Subject rental'
      ].includes(property.propertyType);
      
      if (isRentalType && !property.rentalIncome) {
        errorMsg = errorMsg 
          ? errorMsg + ` Rental Income for Property ${propertyId.replace('property', '')} is required.` 
          : `Rental Income for Property ${propertyId.replace('property', '')} is required.`;
        hasError = true;
        console.log(`[Debug] Missing rental income for property ${propertyId}:`, property);
      }
    });

    // Check borrower names
    activeBorrowers.forEach(borrowerId => {
      if (!formState.borrowers[borrowerId].name) {
         errorMsg = errorMsg
          ? errorMsg + ` Borrower ${borrowerId.replace('borrower', '')} name is required.` 
          : `Borrower ${borrowerId.replace('borrower', '')} name is required.`;
        hasError = true;
        console.log(`[Debug] Missing name for borrower ${borrowerId}`);
      }
    });
    
    if (hasError) {
      console.log('[Debug] Form validation failed:', errorMsg);
      setSubmitError(true);
      setIsSubmitting(false);
      
      // Show error notification
      setNotification({
        show: true,
        message: 'Error - Data not submitted. ' + errorMsg,
        type: 'error'
      });
      
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      console.log('[Debug] Preparing submission data');
      // Transform formState to match DealSubmissionPayload
      const submissionData: DealSubmissionPayload = {
        ...formState,
        downpaymentSource: formState.downpaymentSource,
        downpaymentSourceNotes: formState.downpaymentSourceNotes,
        downpaymentAmount: formState.downpaymentAmount,
        giftor: formState.giftor,
        inheritingFrom: formState.inheritingFrom,
        ltvRestrictions: formState.ltvRestrictions, // Added missing field
      };

      console.log('[Debug] Submission data prepared:', {
        dealNumber: submissionData.dealNumber,
        borrowerCount: Object.keys(submissionData.borrowers).length,
        propertyCount: Object.keys(submissionData.rentalProperties).length,
        incomeCount: Object.keys(submissionData.incomes).length,
        downpaymentSource: submissionData.downpaymentSource,
        downpaymentSourceNotes: submissionData.downpaymentSourceNotes,
        giftor: submissionData.giftor,
        inheritingFrom: submissionData.inheritingFrom
      });

      // Submit form data using the service
      console.log('[Debug] Calling submitBorrowerForm');
      const response = await submitBorrowerForm(submissionData as any); // Temporary type assertion

      console.log('[Debug] Form submission response:', response);
      if (response.success) {
        console.log('[Debug] Form submission successful');
        
        // Show success notification
        setNotification({
          show: true,
          message: 'Data submitted successfully',
          type: 'success'
        });
        
        if (onSubmit) {
          console.log('[Debug] Calling onSubmit callback');
          await onSubmit(submissionData);
          console.log('[Debug] onSubmit callback completed');
        }
      } else {
        console.log('[Debug] Form submission failed:', response.error);
        setSubmitError(true);
        
        // Show error notification
        setNotification({
          show: true,
          message: 'Error - Data not submitted. ' + (response.error || 'Unknown error'),
          type: 'error'
        });
      }
    } catch (error) {
      console.error('[Debug] Form submission error:', error);
      setSubmitError(true);
      
      // Show error notification
      setNotification({
        show: true,
        message: 'Error - Data not submitted. An unexpected error occurred.',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for condition checkbox changes
  const handleConditionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormState(prev => ({
      ...prev,
      conditions: {
        ...prev.conditions,
        [name]: checked,
      },
    }));
  };

  // ----- 6. Add Handlers ----- 
  // Generic handler for top-level text/date inputs
  const handleTopLevelChange = useCallback((fieldName: keyof FormState) => (value: string) => {
    setFormState(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  }, []);

  // Generic handler for top-level checkbox inputs
  const handleTopLevelCheckboxChange = useCallback((fieldName: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormState(prev => ({
      ...prev,
      [fieldName]: event.target.checked,
    }));
  }, []);

  // Generic handler for top-level select/dropdown inputs
  const handleTopLevelSelectChange = useCallback((fieldName: keyof FormState) => (event: SelectChangeEvent) => {
    setFormState(prev => ({
      ...prev,
      [fieldName]: event.target.value,
    }));
  }, []);

  // Handler for Downpayment Source checkbox changes
  const handleDownpaymentSourceChange = (event: SelectChangeEvent) => {
    setFormState(prev => ({
      ...prev,
      downpaymentSource: event.target.value,
    }));
  };

  // Handler for Downpayment Source Notes changes
  const handleDownpaymentSourceNotesChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(prev => ({
      ...prev,
      downpaymentSourceNotes: event.target.value
    }));
  };

  // Handler for Giftor changes
  const handleGiftorChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(prev => ({
      ...prev,
      giftor: event.target.value
    }));
  };

  // Handler for Inheriting From changes
  const handleInheritingFromChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(prev => ({
      ...prev,
      inheritingFrom: event.target.value
    }));
  };

  // ----- ADD BACK Missing Helper Function ----- 
  // Helper function to safely calculate 95% of rental income (avoids eval)
  const calculateNinetyFivePercent = (incomeString: string | undefined | null): string => {
    if (!incomeString) return '0.00';
    // Basic parsing, similar to parseCurrency. Does NOT evaluate expressions.
    const parsed = parseFloat(String(incomeString).replace(/,/g, '')); 
    return isNaN(parsed) ? '0.00' : (parsed * 0.95).toFixed(2);
  };
  // ----- End Added Helper Function -----

  // ----- 3. Fix Hook Dependencies ----- 
  useEffect(() => {
    // Initialize with one income if none exists
    if (activeIncomes.length === 0) { // Added activeIncomes.length dependency below
      const initialIncomeId = 'income1';
      setActiveIncomes([initialIncomeId]);
      setIncomeCounter(1);
      setFormState(prev => ({
        ...prev,
        incomes: {
          [initialIncomeId]: { ...emptyIncome }
        }
      }));
    }
  }, [activeIncomes.length]); // Added dependency

  // ... condition change handler, top level handlers, downpayment handler ...

  // Regex for Canadian postal code validation
  // const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/; // Commented out as currently unused

  // Validate form data
  // ... existing code ...

  // Add a handler for the property checkbox fields
  const handlePropertyCheckboxChange = (propertyId: string, field: keyof RentalProperty) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    
    if (field === 'addThreePercent') {
      // When +3% checkbox is toggled, immediately calculate the value
      let calculatedTaxPlus3Percent = '0.00';
      
      if (checked) {
        const tax = parseFloat(String(formState.rentalProperties[propertyId].propertyTax || '0').replace(/,/g, ''));
        calculatedTaxPlus3Percent = isNaN(tax) ? '0.00' : (tax * 1.03).toFixed(2);
      }
      
      setFormState(prev => ({
        ...prev,
        rentalProperties: {
          ...prev.rentalProperties,
          [propertyId]: {
            ...prev.rentalProperties[propertyId],
            [field]: checked,
            calculatedTaxPlus3Percent
          }
        }
      }));
    } else {
      // For other checkboxes, use the normal update
      setFormState(prev => ({
        ...prev,
        rentalProperties: {
          ...prev.rentalProperties,
          [propertyId]: {
            ...prev.rentalProperties[propertyId],
            [field]: checked
          }
        }
      }));
    }
  };

  // NEW: Handler specifically for the main property type checkboxes
  const handleMainPropertyTypeChange = (propertyId: string, typeValue: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setFormState(prev => {
      const currentType = prev.rentalProperties[propertyId].propertyType;
      // If checking, set to this type. If unchecking *this* type, clear it.
      const newType = isChecked ? typeValue : (currentType === typeValue ? '' : currentType);

      return {
        ...prev,
        rentalProperties: {
          ...prev.rentalProperties,
          [propertyId]: {
            ...prev.rentalProperties[propertyId],
            propertyType: newType
          }
        }
      };
    });
  };

  // Special handler for property type checkbox
  // Commented out as currently unused
  /* const handlePropertyTypeChange = (propertyId: string, value: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handlePropertyChange(propertyId, 'propertyType')({ target: { value } } as React.ChangeEvent<HTMLInputElement>);
    } else {
      handlePropertyChange(propertyId, 'propertyType')({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
    }
  }; */

  // Handler to close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      show: false
    }));
  };

  // Handler for Downpayment Amount changes
  const handleDownpaymentAmountChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(prev => ({
      ...prev,
      downpaymentAmount: event.target.value
    }));
  };

  return (
    <PageBackground sx={{ 
      overflowY: 'auto',
      minHeight: '100vh',
      height: 'auto',
      scrollbarWidth: 'none',  /* Firefox */
      '&::-webkit-scrollbar': {
        display: 'none'  /* Chrome, Safari, Edge */
      },
      msOverflowStyle: 'none'  /* IE and Edge */
    }}>
      {/* REMOVED TEMP Typography */}
      {/* <Typography variant="h6" color="error">TEMP DEBUG: Test Value = {testValue}</Typography> */}
      
      <Typography variant="h5" gutterBottom sx={{ color: '#000', textShadow: '0 1px 2px rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '1.3rem' }}>
        Borrower Form
      </Typography>
      <Container maxWidth={false} sx={{ 
        width: '102%',
        marginLeft: '-1%',
        minHeight: 'fit-content',
        height: 'auto',
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'visible',
        position: 'relative',
        zIndex: 2
      }}>
        <Paper elevation={3} sx={{ 
          padding: { xs: 0.75, sm: 1 }, 
          backgroundColor: 'rgba(255, 255, 255, 0.92)', 
          backdropFilter: 'blur(5px)', 
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          position: 'relative',
          zIndex: 1
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: 'auto', overflow: 'visible' }}>
            {/* Replace the Deal Information section with FormHeader */}
            <FormHeader 
              dealNumber={formState.dealNumber}
              dealType={formState.dealType}
              closingDate={formState.closingDate}
              cof={formState.cof}
              bfsStage={formState.bfsStage}
              startTime={startTime}
              submitError={!!submitError}
              setFormState={setFormState}
              handleTimeUpdate={handleTimeUpdate}
              handleDealTypeChange={handleDealTypeChange}
              handleBfsStageChange={handleBfsStageChange}
            />

            {/* Preliminary Checklist */}
            <PreliminaryChecklist 
              expanded={expandedChecklist}
              onChange={handleChecklistAccordionChange}
              checklistState={{
                propertyWatchlistStatus: formState.propertyWatchlistStatus,
                employerWatchlistStatus: formState.employerWatchlistStatus,
                matchFilesStatus: formState.matchFilesStatus,
                matchFilesNotes: formState.matchFilesNotes,
                propertyTaxAdjustment: formState.propertyTaxAdjustment,
                dealBorrowerRelationshipStatus: formState.dealBorrowerRelationshipStatus, 
                dealBorrowerRelationshipOther: formState.dealBorrowerRelationshipOther,
                propertyValueStatus: formState.propertyValueStatus,
                propertyValueOtherNotes: formState.propertyValueOtherNotes,
                mortgageAmount: formState.mortgageAmount,
                mtgType: formState.mtgType,
                ltv: formState.ltv,
                etoChecked: formState.etoChecked,
                etoNotes: formState.etoNotes,
                term: formState.term,
                rate: formState.rate,
                amortization: formState.amortization,
                ltvRestrictions: formState.ltvRestrictions, 
                propertyInfo: formState.propertyInfo,
                province: formState.province,
              }}
              handleSelectChange={handleTopLevelSelectChange as any} // Pass the handler (cast needed due to key type difference)
              handleTextChange={handleTopLevelChange as any} // Pass the text handler
              handleCheckboxChange={handleTopLevelCheckboxChange as any} // Pass the checkbox handler
              StyledFormControl={StyledFormControl} // Pass the styled component
            />
            
            {/* Scrollable Content Container for the rest of the form */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', paddingRight: '10px' }}>
              {/* Borrower Section */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                  <Typography variant="h5" gutterBottom sx={{ mb: 0 }} fontSize="1.1rem">
                    Borrowers
                  </Typography>
                  {activeBorrowers.length < 4 && (
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />} 
                      onClick={handleAddBorrower}
                      size="small"
                    >
                      Add Borrower
                    </Button>
                  )}
                </Box>
                
                {activeBorrowers.map((borrowerId, index) => (
                  <BorrowerAccordion
                    key={borrowerId}
                    borrowerId={borrowerId}
                    index={index}
                    borrowerData={formState.borrowers[borrowerId]}
                    incomeData={formState.incomes}
                    expanded={expandedBorrower === borrowerId}
                    onChange={handleBorrowerAccordionChange}
                    onRemoveBorrower={handleRemoveBorrower}
                    handleBorrowerChange={handleBorrowerChange}
                    handleIncomeChange={handleIncomeChange}
                    handleAddIncome={handleAddIncome}
                    handleRemoveIncome={handleRemoveIncome}
                    handleIncomeAccordionChange={handleIncomeAccordionChange}
                    activeBorrowersCount={activeBorrowers.length}
                    expandedIncome={expandedIncome}
                    submitError={submitError}
                    setFormState={setFormState}
                    StyledFormControl={StyledFormControl}
                  />
                ))}  
              </Box>

              {/* Properties Section */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                  <Typography variant="h5" gutterBottom sx={{ mb: 0 }} fontSize="1.1rem">
                    Properties
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    onClick={handleAddProperty}
                    size="small"
                  >
                    Add Property
                  </Button>
                </Box>
                
                {activeProperties.map((propertyId) => (
                  <PropertyAccordion
                    key={propertyId}
                    propertyId={propertyId}
                    propertyData={formState.rentalProperties[propertyId]}
                    expanded={expandedProperty === propertyId}
                    onChange={handlePropertyAccordionChange}
                    onRemoveProperty={handleRemoveProperty}
                    handlePropertyChange={handlePropertyChange}
                    StyledFormControl={StyledFormControl}
                  />
                ))}
              </Box>
              
              {/* Downpayment Section */}
              <DownpaymentAccordion 
                formState={formState}
                expanded={expandedSection === 'downpayment'}
                onChange={handleSectionChange('downpayment')}
                handleDownpaymentSourceChange={handleDownpaymentSourceChange}
                handleDownpaymentSourceNotesChange={handleDownpaymentSourceNotesChange}
                handleGiftorChange={handleGiftorChange}
                handleInheritingFromChange={handleInheritingFromChange}
                handleDownpaymentAmountChange={handleDownpaymentAmountChange}
                StyledFormControl={StyledFormControl}
              />
              
              {/* Conditions Section */}
              <ConditionsAccordion 
                formState={formState}
                expanded={expandedSection === 'conditions'}
                onChange={handleSectionChange('conditions')}
                handleConditionChange={handleConditionChange}
                conditionItems={conditionItems}
              />
              
            </Box> {/* End Scrollable Content Container */}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </form>
        </Paper>
        
        {/* Notification Snackbar */}
        <Snackbar 
          open={notification.show} 
          autoHideDuration={6000} 
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.type} 
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </PageBackground>
  );
};

export default BorrowerForm;
